import React, { useState, useEffect, useMemo } from 'react';
import { Icons } from './Icons';
import { db, appId } from '../utils/firebase';
import { collection, onSnapshot } from 'firebase/firestore';
import ImageLightbox from './ImageLightbox';
import VirtualBob from './VirtualBob'; 

// Views
import DashboardView from '../views/DashboardView';
import TimelineView from '../views/TimelineView';
import CalendarView from '../views/CalendarView';
import PersonnelView from '../views/PersonnelView';
import CommsView from '../views/CommsView';
import CipherStudioView from '../views/CipherStudioView';
import RabbitHoleView from '../views/RabbitHoleView';
import MapView from '../views/MapView';
import GenericCardView from '../views/GenericCardView';
import AnalyticsView from '../views/AnalyticsView';
import AssetInventoryView from '../views/AssetInventoryView'; 
import JournalView from '../views/JournalView'; 
import CharacterBibleView from '../views/CharacterBibleView'; 
import FieldManualView from '../views/FieldManualView';
import ShowrunnerOpsView from '../views/ShowrunnerOpsView';
import AnnouncementsView from '../views/AnnouncementsView';

const Toast = ({ message, type }) => {
    if (!message) return null;
    return (
        <div className="fixed bottom-10 left-1/2 transform -translate-x-1/2 z-[2000] pointer-events-none" style={{ animation: 'slideUpFade 4s ease-in-out forwards' }}>
            <div className={`px-6 py-4 rounded shadow-2xl border font-mono text-xs font-bold uppercase tracking-widest flex items-center gap-3 ${type === 'error' ? 'bg-red-950/90 text-red-400 border-red-500/50' : 'bg-green-950/90 text-green-400 border-green-500/50'}`}>
                {type === 'error' ? <Icons.X size={16} /> : <Icons.UserCheck size={16} />}
                {message}
            </div>
        </div>
    );
};

const NavItem = ({ icon, label, active, onClick, glow }) => (
    <button onClick={onClick} title={label} className={`flex items-center gap-3 py-1.5 px-3 w-full rounded-md transition-all ${active ? 'bg-gray-900 text-white border-l-2 border-blue-600' : 'text-gray-500 hover:bg-gray-900 hover:text-gray-300'} ${glow ? 'text-[#00ff41] drop-shadow-[0_0_8px_rgba(0,255,65,0.8)] animate-pulse' : ''}`}>
        {icon} <span className="text-xs font-bold tracking-wide text-left">{label}</span>
    </button>
);

const TertiaryHub = ({ onExit, handle, clearance }) => {
    const getHash = () => window.location.hash.replace('#/', '') || 'dashboard';
    const [activeModule, setActiveModule] = useState(getHash());
    const [isAdminMode, setIsAdminMode] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [toast, setToast] = useState(null);
    
    // UNREAD MESSAGES LOGIC
    const [lastViewedComms, setLastViewedComms] = useState(() => parseInt(localStorage.getItem('ts_last_viewed_comms') || Date.now().toString(), 10));
    
    useEffect(() => {
        const handleHashChange = () => setActiveModule(getHash());
        window.addEventListener('hashchange', handleHashChange);
        return () => window.removeEventListener('hashchange', handleHashChange);
    }, []);

    const handleNavClick = (module) => { 
        window.location.hash = `/${module}`; 
        setIsMobileMenuOpen(false); 
        
        if (module === 'comms') {
            const now = Date.now();
            setLastViewedComms(now);
            localStorage.setItem('ts_last_viewed_comms', now.toString());
        }
    };

    const canBeAdmin = ['LEVEL 4', 'LEVEL 5', 'PILLAR', 'NOSOVEREIGN'].includes(clearance?.toUpperCase());
    const isHighCommand = ['LEVEL 5', 'PILLAR', 'NOSOVEREIGN'].includes(clearance?.toUpperCase());

    const showToast = (message, type = 'error') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 4000);
    };

    const toggleAdminMode = () => {
        if (canBeAdmin) setIsAdminMode(!isAdminMode);
        else showToast("SECURITY OVERRIDE DENIED. L4+ REQUIRED.", "error");
    };

    const [data, setData] = useState({ 
        timeline: [], calendar: [], registry: [], artifacts: [], ideas: [], users: [], ciphers: [], matrix_state: [], matrix: [], comms: [], journals: [], characters: [], ops: [], assets: [], announcements: []
    });

    const [localCiphers, setLocalCiphers] = useState(() => {
        try { return JSON.parse(localStorage.getItem('local_ciphers')) || []; } catch(e) { return []; }
    });

    const [localComms, setLocalComms] = useState(() => {
        try { return JSON.parse(localStorage.getItem('local_comms')) || []; } catch(e) { return []; }
    });

    useEffect(() => {
        if (!appId) return;
        // The account list is admin-only in the database rules, so only admins ask for it.
        const collectionsToFetch = ['timeline', 'calendar', 'registry', 'artifacts', 'ideas', ...(canBeAdmin ? ['users'] : []), 'ciphers', 'matrix_state', 'matrix', 'comms', 'journals', 'characters', 'ops', 'assets', 'announcements'];
        const unsubscribes = collectionsToFetch.map(col => {
            const q = collection(db, 'artifacts', appId, 'public', 'data', col);
            return onSnapshot(q, snap => setData(prev => ({ ...prev, [col]: snap.docs.map(d => ({ id: d.id, ...d.data() })) })), err => console.warn(`Could not fetch '${col}'.`, err));
        });
        return () => unsubscribes.forEach(unsub => unsub());
    }, []);

    // --- NATIVE NOTIFICATION PERMISSION REQUEST ---
    useEffect(() => {
        if ("Notification" in window && Notification.permission !== "granted" && Notification.permission !== "denied") {
            Notification.requestPermission();
        }
    }, []);

    // --- CALCULATE IF MENU SHOULD GLOW AND TRIGGER NOTIFICATIONS ---
    const hasUnreadComms = useMemo(() => {
        const allMsgs = data.comms || [];
        if (allMsgs.length === 0) return false;
        
        // Sort to find the absolute newest message
        const sorted = [...allMsgs].sort((a, b) => (b.timestamp?.toMillis() || 0) - (a.timestamp?.toMillis() || 0));
        const newestMsg = sorted[0];
        const latestTime = newestMsg.timestamp?.toMillis() || 0;
        
        if (latestTime > lastViewedComms) {
            
            // If the message is new AND wasn't sent by the person currently logged in, pop a native notification!
            if (newestMsg.handle !== handle && "Notification" in window && Notification.permission === "granted") {
                // Prevent duplicate notifications by tracking the last one we showed
                const lastNotified = parseInt(sessionStorage.getItem('ts_last_notified') || '0', 10);
                if (latestTime > lastNotified) {
                    const plainText = newestMsg.text ? String(newestMsg.text).replace(/<[^>]*>?/gm, '') : 'New transmission.';
                    new Notification("Tertiary Hub - Secure Comms", {
                        body: `${newestMsg.handle}: ${plainText}`,
                        icon: '/favicon.ico'
                    });
                    sessionStorage.setItem('ts_last_notified', latestTime.toString());
                }
            }
            
            // If they aren't currently looking at the Comms tab, make the menu glow
            if (activeModule !== 'comms') {
                return true;
            } else {
                // If they are looking at the Comms tab when the message arrives, automatically update their read receipt
                setLastViewedComms(Date.now());
                localStorage.setItem('ts_last_viewed_comms', Date.now().toString());
                return false;
            }
        }
        return false;
    }, [data.comms, lastViewedComms, activeModule, handle]);

    const renderContent = () => {
        const isDoor = (item) => {
            const str = String(item.name || item.title || '').toLowerCase() + String(item.designation || '').toLowerCase() + String(item.type || '').toLowerCase();
            return str.includes('door') || str.includes('portal') || str.includes('sector') || str.includes('hist') || str.includes('hinge');
        };

        const enhancedCiphers = [...(data.ciphers || []), ...localCiphers];
        const sharedProps = { isAdmin: isAdminMode, showToast: showToast, dbData: data, data: data, userData: { handle, clearance, uid: handle }, onNavigate: handleNavClick };

        switch(activeModule) {
            case 'dashboard': return <DashboardView {...sharedProps} />;
            case 'manual': return <FieldManualView {...sharedProps} />;
            case 'timeline': return <TimelineView {...sharedProps} />;
            case 'calendar': return <CalendarView {...sharedProps} />;
            case 'announcements': return <AnnouncementsView {...sharedProps} />;
            case 'personnel': return <PersonnelView {...sharedProps} registryData={data.registry || []} usersData={data.users || []} />;
            case 'comms': 
                return <CommsView 
                    {...sharedProps} 
                    localComms={localComms}
                    setLocalComms={setLocalComms}
                />;
            case 'cipher': return <CipherStudioView {...sharedProps} localCiphers={localCiphers} setLocalCiphers={setLocalCiphers} />;
            case 'matrix': return <RabbitHoleView {...sharedProps} ciphers={enhancedCiphers} allNodes={[...(data.artifacts || []), ...(data.ideas || []), ...(data.journals || [])]} />;
            case 'map': return <MapView {...sharedProps} />;
            case 'doors': return <GenericCardView {...sharedProps} title="Door Archive" icon={Icons.DoorOpen} colorClass="text-purple-600" type="ARTIFACT" filterFn={isDoor} defaultData={{ designation: 'HIST-DOOR' }} />;
            case 'artifacts': return <GenericCardView {...sharedProps} title="Artifact Archive" icon={Icons.Box} colorClass="text-orange-600" type="ARTIFACT" filterFn={(i) => !isDoor(i)} />;
            case 'lore': return <GenericCardView {...sharedProps} title="Lore & Concepts" icon={Icons.BookOpen} colorClass="text-indigo-500" type="IDEA" defaultData={{ tag: 'LORE' }} />;
            case 'inventory': return <AssetInventoryView {...sharedProps} />;
            case 'journal': return <JournalView {...sharedProps} />;
            case 'characters': return <CharacterBibleView {...sharedProps} />;
            case 'ops': return <ShowrunnerOpsView {...sharedProps} />;
            case 'analytics': 
                if (!isHighCommand) return <div className="flex-1 flex flex-col items-center justify-center p-10 text-center bg-[#050505]"><Icons.Unlock size={48} className="text-red-900 mb-4" /><h2 className="text-2xl font-black text-white tracking-widest uppercase mb-2">RESTRICTED DOMAIN</h2><p className="text-red-500 text-xs font-bold tracking-widest uppercase">LEVEL 5 CLEARANCE REQUIRED</p></div>;
                if (!isAdminMode) return <div className="flex-1 flex flex-col items-center justify-center p-10 text-center bg-[#050505] animate-pulse"><Icons.EyeOff size={48} className="text-gray-800 mb-4" /><h2 className="text-2xl font-black text-gray-500 tracking-widest uppercase mb-2">AWAITING OVERRIDE</h2><p className="text-gray-600 text-xs font-bold tracking-widest uppercase">ACTIVATE EYE PROTOCOL</p></div>;
                return <AnalyticsView {...sharedProps} />;
            default: return <div className="p-10 text-gray-500 font-mono border border-dashed border-gray-800 m-6 text-center">MODULE PENDING CONSTRUCTION...</div>;
        }
    };

    return (
        <div className="h-[100dvh] bg-black text-gray-100 font-sans flex flex-col overflow-hidden fade-in relative">
            <Toast message={toast?.message} type={toast?.type} />
            <ImageLightbox />
            <VirtualBob activeModule={activeModule} userName={handle} />

            <div className="flex flex-col shrink-0 z-[100] shadow-lg">
                <header className="border-b border-gray-900 p-4 flex justify-between items-center bg-black relative">
                    <div className="flex items-center gap-4">
                        <button className="md:hidden text-gray-400 hover:text-white p-1" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}><Icons.Menu size={24} /></button>
                        <button onClick={onExit} className="p-2 bg-black rounded-md border border-gray-900 h-12 w-12 flex items-center justify-center text-white hover:text-red-600 hover:border-red-900 transition-all group" title="Sever Connection">
                            <Icons.TriangleLogo className="w-6 h-6 group-hover:rotate-180 transition-transform duration-500" />
                        </button>
                        <div>
                            <h1 className="text-lg font-black tracking-tighter text-white uppercase">Tertiary<span className="text-gray-600">Hub</span></h1>
                            <p className="text-[10px] text-blue-600 font-bold tracking-widest uppercase hidden sm:block">v1.6.0 {'//'} {activeModule} {'//'} {isAdminMode ? 'Override' : 'Secure'}</p>
                        </div>
                    </div>
                    <div className="text-right mr-2 md:mr-4">
                        <div className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">{clearance || 'Operative'}</div>
                        <div className="text-xs font-bold text-white">{handle || 'Anon'}</div>
                    </div>
                </header>

                <div className="md:hidden bg-black border-b border-gray-900 flex justify-between">
                    <button onClick={() => handleNavClick('dashboard')} className={`flex-1 flex flex-col items-center justify-center p-3 gap-1 transition-colors ${activeModule === 'dashboard' ? 'text-blue-500' : 'text-gray-500'}`}>
                        <Icons.Home size={18} /><span className="text-[8px] font-bold uppercase tracking-widest">Home</span>
                    </button>
                    <button onClick={() => handleNavClick('timeline')} className={`flex-1 flex flex-col items-center justify-center p-3 gap-1 transition-colors ${activeModule === 'timeline' ? 'text-blue-500' : 'text-gray-500'}`}>
                        <Icons.Activity size={18} /><span className="text-[8px] font-bold uppercase tracking-widest">Pulse</span>
                    </button>
                    <button onClick={() => handleNavClick('comms')} className={`flex-1 flex flex-col items-center justify-center p-3 gap-1 transition-colors ${hasUnreadComms ? 'text-[#00ff41] animate-pulse drop-shadow-[0_0_8px_rgba(0,255,65,0.8)]' : (activeModule === 'comms' ? 'text-blue-500' : 'text-gray-500')}`}>
                        <Icons.Mail size={18} /><span className="text-[8px] font-bold uppercase tracking-widest">Comms</span>
                    </button>
                    <button onClick={toggleAdminMode} className={`flex-1 flex flex-col items-center justify-center p-3 gap-1 transition-colors ${isAdminMode ? 'text-cyan-400' : 'text-gray-500'}`}>
                        {isAdminMode ? <Icons.Eye size={18} className="drop-shadow-[0_0_8px_rgba(6,182,212,0.8)]" /> : <Icons.EyeOff size={18} />}
                        <span className={`text-[8px] font-bold uppercase tracking-widest ${isAdminMode ? 'text-cyan-400' : ''}`}>Override</span>
                    </button>
                </div>
            </div>

            <div className="flex flex-1 overflow-hidden relative">
                {isMobileMenuOpen && <div className="fixed inset-0 bg-black/80 z-[200] md:hidden" onClick={() => setIsMobileMenuOpen(false)} />}
                
                <aside className={`fixed md:relative inset-y-0 left-0 w-64 border-r border-gray-900 bg-black flex flex-col z-[300] transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
                    <div className="h-16 md:hidden border-b border-gray-900 flex items-center justify-between px-4 shrink-0">
                         <span className="font-black text-white tracking-widest uppercase text-xs">Navigation Control</span>
                         <button onClick={() => setIsMobileMenuOpen(false)} className="text-gray-500"><Icons.X size={20}/></button>
                    </div>

                    <nav className="flex flex-col gap-0.5 p-3 flex-1 overflow-y-auto custom-scrollbar min-h-0 pb-20 md:pb-3">
                        <NavItem icon={<Icons.Home size={18} />} label="Dashboard" active={activeModule === 'dashboard'} onClick={() => handleNavClick('dashboard')} />
                        <NavItem icon={<Icons.BookOpen size={18} />} label="CRI Field Manual" active={activeModule === 'manual'} onClick={() => handleNavClick('manual')} />
                        <div className="my-1.5 border-t border-gray-900"></div>
                        <NavItem icon={<Icons.Monitor size={18} />} label="Showrunner Ops" active={activeModule === 'ops'} onClick={() => handleNavClick('ops')} />
                        <NavItem icon={<Icons.Activity size={18} />} label="Timeline Pulse" active={activeModule === 'timeline'} onClick={() => handleNavClick('timeline')} />
                        <NavItem icon={<Icons.Calendar size={18} />} label="Calendar" active={activeModule === 'calendar'} onClick={() => handleNavClick('calendar')} />
                        <NavItem icon={<Icons.MessageSquare size={18} />} label="Public Updates Blog" active={activeModule === 'announcements'} onClick={() => handleNavClick('announcements')} />
                        <div className="my-1.5 border-t border-gray-900"></div>
                        <NavItem icon={<Icons.Users size={18} />} label="Personnel" active={activeModule === 'personnel'} onClick={() => handleNavClick('personnel')} />
                        <NavItem icon={<Icons.Mail size={18} />} label="Messaging" active={activeModule === 'comms'} glow={hasUnreadComms} onClick={() => handleNavClick('comms')} />
                        <div className="my-1.5 border-t border-gray-900"></div>
                        <NavItem icon={<Icons.PenTool size={18} />} label="Cipher Studio" active={activeModule === 'cipher'} onClick={() => handleNavClick('cipher')} />
                        <NavItem icon={<Icons.Network size={18} />} label="Rabbit Hole Matrix" active={activeModule === 'matrix'} onClick={() => handleNavClick('matrix')} />
                        <NavItem icon={<Icons.MapPin size={18} />} label="Geospatial Tracking" active={activeModule === 'map'} onClick={() => handleNavClick('map')} />
                        <div className="my-1.5 border-t border-gray-900"></div>
                        <NavItem icon={<Icons.DoorOpen size={18} />} label="Door Archive" active={activeModule === 'doors'} onClick={() => handleNavClick('doors')} />
                        <NavItem icon={<Icons.Box size={18} />} label="Artifact Archive" active={activeModule === 'artifacts'} onClick={() => handleNavClick('artifacts')} />
                        <NavItem icon={<Icons.BookOpen size={18} />} label="Lore & Concepts" active={activeModule === 'lore'} onClick={() => handleNavClick('lore')} />
                        <NavItem icon={<Icons.User size={18} />} label="Character Bible" active={activeModule === 'characters'} onClick={() => handleNavClick('characters')} />
                        <div className="my-1.5 border-t border-gray-900"></div>
                        <NavItem icon={<Icons.Database size={18} />} label="Assets & Logistics" active={activeModule === 'inventory'} onClick={() => handleNavClick('inventory')} />
                        <NavItem icon={<Icons.BookOpen size={18} />} label="Temporal Logs" active={activeModule === 'journal'} onClick={() => handleNavClick('journal')} />

                        {isHighCommand && (
                            <>
                                <div className="my-1.5 border-t border-gray-900"></div>
                                <NavItem icon={<Icons.BarChart size={18} />} label="System Analytics" active={activeModule === 'analytics'} onClick={() => handleNavClick('analytics')} />
                            </>
                        )}
                    </nav>

                    <div className="p-4 border-t border-gray-900 space-y-2 bg-black shrink-0 md:pb-4">
                        <div className={`flex items-center justify-between bg-[#0a0a0a] p-3 rounded border transition-colors ${isAdminMode ? 'border-cyan-900/50' : 'border-gray-900'}`}>
                            <div><span className="text-[9px] text-gray-500 font-bold uppercase tracking-widest block">Controls</span><span className={`text-[8px] font-mono mt-0.5 ${isAdminMode ? 'text-cyan-500' : 'text-gray-700'}`}>{isAdminMode ? 'OVERRIDE' : 'SECURED'}</span></div>
                            <button onClick={toggleAdminMode} className={`p-2 rounded transition-colors ${isAdminMode ? 'text-cyan-400 bg-cyan-900/20 border-cyan-500/50 shadow-[0_0_15px_rgba(6,182,212,0.3)]' : (canBeAdmin ? 'text-gray-500 hover:text-white' : 'text-red-900/50 cursor-not-allowed')}`} title={canBeAdmin ? "Toggle Admin Edit Mode" : "Clearance Denied"}>{isAdminMode ? <Icons.Eye size={16} /> : <Icons.EyeOff size={16} />}</button>
                        </div>
                    </div>
                </aside>

                <main className="flex-1 bg-[#050505] overflow-y-auto relative flex flex-col h-full z-10">{renderContent()}</main>
            </div>
        </div>
    );
};

export default TertiaryHub;